import { useState, useEffect } from 'react';
import type { StrategyMode } from '../hooks/useOnboardingState';
import { API_BASE_URL } from '../../../services/api';
import ObservancePicker from '../../../components/observances/ObservancePicker';

interface Step3ContentPlanningProps {
  strategyMode: StrategyMode;
  allocationPercent: number;
  observanceEnabled: boolean;
  selectedObservances: string[];
  observanceError: string;
  planCap?: number;
  planName?: string;
  country: string;
  onSetCountry: (val: string) => void;
  onSetSelectedObservances: (val: string[]) => void;
  onSetStrategy: (mode: StrategyMode) => void;
  onSetAllocationPercent: (val: number) => void;
  onSetObservanceEnabled: (val: boolean) => void;
  onToggleObservance: (obs: string) => void;
  onSelectAllObservances: (candidateKeys: string[]) => void;
  /** Observance picks allowed for the subscription tenure (from the server, by plan). */
  maxObservances: number;
  onSetMaxObservances: (max: number) => void;
  onClearObservanceError: () => void;
}

export default function Step3ContentPlanning({
  strategyMode,
  allocationPercent,
  observanceEnabled,
  selectedObservances,
  observanceError,
  planCap,
  planName,
  country,
  onSetCountry,
  onSetSelectedObservances,
  onSetStrategy,
  onSetAllocationPercent,
  onSetObservanceEnabled,
  onToggleObservance,
  onSelectAllObservances,
  maxObservances,
  onSetMaxObservances,
  onClearObservanceError,
}: Step3ContentPlanningProps) {
  const [sliderMax, setSliderMax] = useState(100);
  // Setup offers the observances of the rest of the subscription tenure (a month or a quarter).
  const loadSetupObservances = async (c: string) => {
    const userId = localStorage.getItem('user_id') || '';
    const token = localStorage.getItem('access_token');
    const response = await fetch(
      `${API_BASE_URL}/smm/onboarding/observances?country=${c}&user_id=${userId}`,
      { headers: token ? { Authorization: `Bearer ${token}` } : {} }
    );
    return response.json();
  };

  // Mock tier logic: Dynamic tier logic based on user plan cap
  useEffect(() => {
    if (strategyMode === 'regular') setSliderMax(100);
    else if (strategyMode === 'campaign') setSliderMax(100);
    else setSliderMax(planCap ?? 35);
  }, [strategyMode, planCap]);

  const handleSliderChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const val = Number(e.target.value);
    if (strategyMode === 'hybrid') {
      onSetAllocationPercent(Math.min(val, sliderMax));
    } else {
      onSetAllocationPercent(val);
    }
  };

  const campaignPct = strategyMode === 'campaign' ? 100 : (strategyMode === 'regular' ? 0 : allocationPercent);
  const regularPct = strategyMode === 'regular' ? 100 : (strategyMode === 'campaign' ? 0 : 100 - allocationPercent);

  return (
    <div className="space-y-8 animate-fade-in">
      {/* Header */}
      <div className="text-center mb-2">
        {/* <h2 className="text-xl font-bold text-gray-900 mb-1">Content Planning Model Configuration</h2> */}
        <p className="text-sm text-gray-500">Define your content distribution algorithm and observance engine</p>
      </div>

      {/* Section 1: Strategy Cards */}
      <div className="bg-white rounded-xl border border-gray-200 p-6">
        <div className="flex items-center gap-2 mb-4">
          <div className="w-7 h-7 bg-teal-50 rounded-lg flex items-center justify-center">
            <i className="ri-settings-3-line text-teal-600 text-sm"></i>
          </div>
          <h3 className="text-sm font-semibold text-gray-800">Content Strategy Selection</h3>
          <span className="text-[10px] text-gray-400 ml-auto">Choose one or both</span>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {/* Card A: Regular */}
          <button
            onClick={() => {
              if (strategyMode === 'campaign') onSetStrategy('hybrid');
              else if (strategyMode === 'hybrid') onSetStrategy('campaign');
            }}
            className={`relative p-5 rounded-xl border text-left transition-all cursor-pointer ${strategyMode === 'regular' || strategyMode === 'hybrid'
                ? 'border-teal-300 bg-teal-50/50 ring-1 ring-teal-200'
                : 'border-gray-200 bg-white hover:border-gray-300'
              }`}
          >
            <div className="flex items-start justify-between mb-3">
              <div className="w-10 h-10 bg-teal-100 rounded-lg flex items-center justify-center">
                <i className="ri-stack-line text-teal-600 text-lg"></i>
              </div>
              <div
                className={`w-5 h-5 rounded-full border flex items-center justify-center transition-colors ${strategyMode === 'regular' || strategyMode === 'hybrid'
                    ? 'bg-teal-500 border-teal-500'
                    : 'border-gray-300'
                  }`}
              >
                {(strategyMode === 'regular' || strategyMode === 'hybrid') && (
                  <i className="ri-check-line text-white text-xs"></i>
                )}
              </div>
            </div>
            <h4 className="text-sm font-semibold text-gray-800 mb-1">Regular Content Strategy</h4>
            <p className="text-xs text-gray-500 leading-relaxed">
              Individual standalone posts built around recurring themes, product/service updates, educational insights,
              and community engagement.
            </p>
          </button>

          {/* Card B: Campaign */}
          <button
            onClick={() => {
              if (strategyMode === 'regular') onSetStrategy('hybrid');
              else if (strategyMode === 'hybrid') onSetStrategy('regular');
            }}
            className={`relative p-5 rounded-xl border text-left transition-all cursor-pointer ${strategyMode === 'campaign' || strategyMode === 'hybrid'
                ? 'border-teal-300 bg-teal-50/50 ring-1 ring-teal-200'
                : 'border-gray-200 bg-white hover:border-gray-300'
              }`}
          >
            <div className="flex items-start justify-between mb-3">
              <div className="w-10 h-10 bg-amber-100 rounded-lg flex items-center justify-center">
                <i className="ri-links-line text-amber-600 text-lg"></i>
              </div>
              <div
                className={`w-5 h-5 rounded-full border flex items-center justify-center transition-colors ${strategyMode === 'campaign' || strategyMode === 'hybrid'
                    ? 'bg-teal-500 border-teal-500'
                    : 'border-gray-300'
                  }`}
              >
                {(strategyMode === 'campaign' || strategyMode === 'hybrid') && (
                  <i className="ri-check-line text-white text-xs"></i>
                )}
              </div>
            </div>
            <h4 className="text-sm font-semibold text-gray-800 mb-1">Campaign-Based Content Strategy</h4>
            <p className="text-xs text-gray-500 leading-relaxed">
              Interconnected multi-post sequence arcs built around a single launch initiative, storytelling flow, or
              promotional anchor.
            </p>
          </button>
        </div>
      </div>

      {/* Allocation Slider */}
      {strategyMode === 'hybrid' && (
        <div className="bg-white rounded-xl border border-gray-200 p-6">
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center gap-2">
              <div className="w-7 h-7 bg-indigo-50 rounded-lg flex items-center justify-center">
                <i className="ri-percent-line text-indigo-600 text-sm"></i>
              </div>
              <h3 className="text-sm font-semibold text-gray-800">Dynamic Cap Allocation</h3>
            </div>
            <span className="text-[10px] bg-gray-100 text-gray-500 px-2 py-0.5 rounded-full font-medium">
              Active Allocation: {planName ?? 'Silver'} Tier — Campaign Cap Locked at Max {sliderMax}%
            </span>
          </div>

          {/* Current Allocation Display */}
          <div className="flex justify-between items-center mb-4 px-1">
            <div className="flex items-center gap-2 bg-sky-50 border border-sky-100 rounded-lg px-3 py-1.5 shadow-sm">
              <span className="w-2.5 h-2.5 rounded-full bg-sky-500 animate-pulse"></span>
              <span className="text-xs font-semibold text-sky-800">
                {campaignPct}% Campaign Content
              </span>
            </div>
            <div className="flex items-center gap-2 bg-blue-50 border border-blue-100 rounded-lg px-3 py-1.5 shadow-sm">
              <span className="w-2.5 h-2.5 rounded-full bg-blue-500 animate-pulse"></span>
              <span className="text-xs font-semibold text-blue-800">
                {regularPct}% Regular Content
              </span>
            </div>
          </div>

          {/* Slider Control */}
          <div className="flex items-center gap-4">
            <div className="text-xs font-semibold text-sky-600 w-20 text-right whitespace-nowrap">Campaign</div>
            <div className="flex-1 relative py-2 pb-6">
              {/* Hybrid needs 1–99% campaign content; the backend rejects 0. */}
              <input
                type="range"
                min={1}
                max={100}
                value={allocationPercent}
                onChange={handleSliderChange}
                style={{
                  background: `linear-gradient(to right, #0ea5e9 0%, #0ea5e9 ${allocationPercent}%, #2563eb ${allocationPercent}%, #2563eb 100%)`,
                }}
                className="w-full h-3 rounded-full appearance-none cursor-pointer outline-none transition-all shadow-inner border border-gray-100
                  [&::-webkit-slider-thumb]:appearance-none 
                  [&::-webkit-slider-thumb]:w-5 
                  [&::-webkit-slider-thumb]:h-5 
                  [&::-webkit-slider-thumb]:rounded-full 
                  [&::-webkit-slider-thumb]:bg-white 
                  [&::-webkit-slider-thumb]:border-4 
                  [&::-webkit-slider-thumb]:border-blue-600 
                  [&::-webkit-slider-thumb]:shadow-md 
                  [&::-webkit-slider-thumb]:cursor-pointer 
                  [&::-webkit-slider-thumb]:hover:scale-110 
                  [&::-webkit-slider-thumb]:transition-all
                  
                  [&::-moz-range-thumb]:w-5 
                  [&::-moz-range-thumb]:h-5 
                  [&::-moz-range-thumb]:rounded-full 
                  [&::-moz-range-thumb]:bg-white 
                  [&::-moz-range-thumb]:border-4 
                  [&::-moz-range-thumb]:border-blue-600 
                  [&::-moz-range-thumb]:shadow-md 
                  [&::-moz-range-thumb]:cursor-pointer 
                  [&::-moz-range-thumb]:hover:scale-110 
                  [&::-moz-range-thumb]:transition-all"
              />
              
              {/* Max Cap Limit Marker Line */}
              <div 
                className="absolute top-1/2 -translate-y-1/2 w-0.5 h-5 bg-rose-500 z-10 pointer-events-none shadow"
                style={{ left: `${sliderMax}%` }}
                title={`Campaign Cap limit: ${sliderMax}%`}
              >
                <span className="absolute bottom-full left-1/2 -translate-x-1/2 mb-1.5 bg-rose-500 text-white text-[9px] font-bold px-1.5 py-0.5 rounded shadow whitespace-nowrap">
                  Max {sliderMax}% Cap
                </span>
              </div>

              {/* Absolute Scale Guides */}
              <div className="absolute top-full left-0 right-0 mt-1 text-[10px]">
                <span className="absolute left-0 text-sky-600 font-semibold">0% Campaign</span>
                <span 
                  className="absolute -translate-x-1/2 text-rose-500 font-bold"
                  style={{ left: `${sliderMax}%` }}
                >
                  {sliderMax}% Max Cap
                </span>
                <span className="absolute right-0 text-gray-300 font-semibold">100%</span>
              </div>
            </div>
            <div className="text-xs font-semibold text-blue-600 w-20 whitespace-nowrap">Regular</div>
          </div>
        </div>
      )}

      {/* Section 2: Social Observance */}
      <ObservancePicker
        title="Social Observance Allocation Hub"
        cycleLabel="Subscription"
        enabled={observanceEnabled}
        onEnabledChange={onSetObservanceEnabled}
        country={country}
        onCountryChange={onSetCountry}
        selected={selectedObservances}
        onToggle={onToggleObservance}
        onSelectMany={onSelectAllObservances}
        max={maxObservances}
        onMaxChange={onSetMaxObservances}
        error={observanceError}
        onClearError={onClearObservanceError}
        loadObservances={loadSetupObservances}
      />
    </div>
  );
}